import React, { useRef, useContext, useState, useCallback, useMemo, type JSX } from 'react';
import { EDCard } from '../../../../../../BaseComponents/Card/EDCard';
import EDMasonryItem from '../../../../../../BaseComponents/Masonry/EDMasonryItem';
import { makeStyles } from 'tss-react/mui';
import GroupHeader from '../GroupHeader/GroupHeader';
import { ObjektVyGruppContextProvider } from './ObjektVyKortContext';
import { VisningsTypContext } from '../VisningsTypContext/VisningsTypContext';
import ReadOnlyFormContextProvider from '../BusinessDataForm/ReadOnlyFormContextProvider/ReadOnlyFormContextProvider';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { IObjektVy, IParentInfo } from '../../../../../../Types/ObjektVy';
import { vyDefToCaptionedComp } from '../../../../../../Utils/ObjektVyCaptionUtils';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import { ObjektVyFaltLista } from '../ObjektVyFaltLista/ObjektVyFaltLista';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { CommonPropsAndTypes } from '../../../../../../BaseComponents/_Types/StandardComponentProps';

export interface IObjektVyGruppProps {
  caption?: string;
  readOnly?: boolean;
  backgroundColor?: string;
  background?: string;
  noMargin?: boolean;
  className?: string;
  //från objektvygruppcomp
  id?: TSelectedDetail;
  config?: { fullPage?: boolean; parents: IParentInfo[] };
  button?: () => JSX.Element;
  bmod?: IClientBusinessModel;
  /** lista av vyer (en sträng kan användas som genväg för att skapa en fältvy) */
  vyer?: Array<IObjektVy | string>;
  /** Visa hela gruppen som loading */
  loading?: boolean;
  noPadding?: boolean;
  cardInCard?: boolean;
  cardFullHeight?: boolean;
  overflowAuto?: boolean;
  cardFlex?: boolean;
  masonryItemPaddingBottom?: CommonPropsAndTypes.StandardSpacingType;
  hideHeaderMenu?: boolean;
  border?: string;
  noBoxShadow?: boolean;
  width?: string;
}

const useStyles = makeStyles()(theme => ({
  spaced: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
  },
  spacedWithMenu: {
    height: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  captionOnlyPadding: {
    //om noPadding används så sätts den här för att ge rubriken en egen padding
    padding: theme.spacing(2, 2, 0, 2),
  },
  menuContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  overflowAuto: {
    overflow: 'auto',
  },
}));

/** Container för knapparna uppe till höger i kortet */
const ButtonContainer: React.FC<React.PropsWithChildren<{}>> = props => (
  <EDBox sx={{ display: 'flex', flex: '1 1 50%', justifyContent: 'flex-end' }}>
    {props.children}
  </EDBox>
);

/**
Komponent som visar en lista av objektvyer för samma objekt
*/
export const ObjektVyKort: React.FunctionComponent<
  React.PropsWithChildren<IObjektVyGruppProps>
> = props => {
  const { classes, cx } = useStyles(undefined, {
    props: props as any,
  });
  const [menuRef, setMenuRefBase] = useState<
    React.MutableRefObject<HTMLDivElement | null>
  >({ current: null });
  const legendRef = useRef<HTMLDivElement | null>(null);
  const visningsTypContext = useContext(VisningsTypContext);

  const setMenuRef = useCallback(
    (node: any) => {
      setMenuRefBase({ current: node });
    },
    [setMenuRefBase]
  );

  const Comps = useMemo(
    () =>
      props.bmod && props.vyer
        ? props.vyer.map(vyDefToCaptionedComp(props.bmod))
        : undefined,
    [props.bmod, props.vyer]
  );
  return (
    <ReadOnlyFormContextProvider disabled={!props.readOnly}>
      <ObjektVyGruppContextProvider
        value={{ menuRef: menuRef, legendRef: legendRef }}
      >
        <EDMasonryItem
          paddingBottom={
            props.masonryItemPaddingBottom ??
            (!visningsTypContext.showDetailsRight ? 'normal' : undefined)
          }
          classes={{
            item: cx({
              [classes.overflowAuto]: props.overflowAuto,
            }),
          }}
        >
          <EDCard
            padding={props.noPadding ? 0 : 2}
            cardInCard={visningsTypContext.showDetailsRight || props.cardInCard}
            backgroundColor={props.backgroundColor}
            background={props.background}
            elevation={visningsTypContext.showDetailsRight ? 0 : undefined}
            height={props.cardFullHeight && '100%'}
            boxSizing={props.cardFullHeight && 'border-box'}
            flex={props.cardFlex}
            border={props.border}
            noBoxShadow={props.noBoxShadow}
          >
            {props.caption && (
              <div
                className={cx(classes.spaced, {
                  [classes.spacedWithMenu]:
                    menuRef.current !== null || !!props.button,
                  [classes.captionOnlyPadding]: !!props.noPadding,
                })}
              >
                <GroupHeader width={props.width}>{props.caption}</GroupHeader>

                <ButtonContainer>
                  {props.button && props.button()}
                  {!props.hideHeaderMenu && (
                    <>
                      <div ref={legendRef} />
                      <div ref={setMenuRef} className={classes.menuContainer} />
                    </>
                  )}
                </ButtonContainer>
              </div>
            )}
            {props.vyer && Comps && props.id ? (
              <ObjektVyFaltLista>
                {props.loading
                  ? `${props.caption} laddas`
                  : props.vyer.map((vyDef, idx) => {
                      const Comp = Comps[idx];

                      const key =
                        typeof vyDef === 'string'
                          ? vyDef
                          : `${vyDef.type}-${vyDef.id}-${props.id}`;
                      return (
                        <Comp
                          key={key}
                          id={props.id || 'notanid'}
                          config={{ parents: props.config?.parents ?? [] }}
                        />
                      );
                    })}
              </ObjektVyFaltLista>
            ) : (
              props.children
            )}
          </EDCard>
        </EDMasonryItem>
      </ObjektVyGruppContextProvider>
    </ReadOnlyFormContextProvider>
  );
};

// export default ObjektVyKort;
